import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { analyzeProfile, saveAnalysisToHistory } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Card from '../components/Card';

const MainContent = styled.main`
  padding: 20px 0;
  width: 100%;
  max-width: 100%;
  position: relative;
  overflow-x: hidden;
`;

const UploadSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const PhoneMockup = styled(Card)`
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  height: 600px;
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 0;
  background-color: rgba(25, 0, 50, 0.2);
  backdrop-filter: blur(10px);
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 64, 192, 0.1) 0%, rgba(255, 208, 0, 0.05) 100%);
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 
      0 15px 30px rgba(0, 0, 0, 0.3),
      0 0 15px rgba(255, 64, 192, 0.15);
  }
`;

const PhoneHeader = styled.div`
  background-color: rgba(0, 0, 0, 0.25);
  padding: 15px;
  text-align: center;
  color: #ffffff;
  font-size: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  i {
    color: var(--primary-color);
    font-size: 1.1rem;
  }
`;

const PhoneContent = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-content: flex-start;
  position: relative;
  background-color: rgba(0, 0, 0, 0.1);
  
  &::-webkit-scrollbar {
    width: 5px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--primary-color);
    border-radius: 10px;
  }
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 64, 192, 0.05) 0%, rgba(255, 208, 0, 0.03) 100%);
    pointer-events: none;
    z-index: 0;
  }
`;

const UploadPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
  border-radius: 25px;
  
  &.drag-over {
    background-color: rgba(255, 64, 192, 0.15);
  }
  
  i {
    font-size: 60px;
    margin-bottom: 20px;
    color: rgba(255, 255, 255, 0.4);
    transition: all 0.3s ease;
    filter: drop-shadow(0 0 10px rgba(255, 105, 180, 0.3));
  }
  
  p {
    font-size: 15px;
    line-height: 1.5;
    max-width: 80%;
    text-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  }
  
  &:hover {
    color: rgba(255, 255, 255, 0.9);
    
    i {
      color: var(--primary-color);
      transform: translateY(-5px) scale(1.05);
    }
  }
`;

const ImagePreview = styled.div`
  width: calc(50% - 5px);
  aspect-ratio: 1;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 8px;
  
  @media (max-width: 600px) {
    width: calc(100% - 5px);
  }
`;

const PhoneFooter = styled.div`
  padding: 15px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.25);
`;

const ActionButton = styled.div`
  padding: 10px 25px;
  background: linear-gradient(45deg, var(--primary-color) 0%, #ff65d5 100%);
  color: #ffffff;
  border-radius: 30px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 64, 192, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(255, 64, 192, 0.5);
  }
  
  i {
    font-size: 0.9em;
  }
`;

const InputSection = styled(Card)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 25px;
  color: #ffffff;
  font-family: 'Bubblegum Sans', 'Poppins', sans-serif;
  text-align: center;
  text-shadow: 0 2px 10px rgba(255, 64, 192, 0.2);
`;

const InputGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  text-align: left;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 15px;
  background-color: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: #ffffff;
  font-size: 1rem;
  font-family: 'Poppins', sans-serif;
  outline: none;
  transition: border-color 0.3s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23FFFFFF%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
  background-repeat: no-repeat;
  background-position: right 15px center;
  background-size: 12px;
  padding-right: 30px;
  
  &:focus {
    border-color: #ff3eb5;
  }
  
  option {
    background-color: #2c1249;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  background-color: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: #ffffff;
  font-size: 1rem;
  font-family: 'Poppins', sans-serif;
  resize: none;
  min-height: 140px;
  outline: none;
  transition: border-color 0.3s ease;
  overflow: hidden;
  
  &:focus {
    border-color: var(--primary-color);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const DisclaimerBox = styled.div`
  margin-top: 0;
  margin-bottom: 20px;
  padding: 12px 18px;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  border-left: 3px solid var(--primary-color);
  font-size: 0.85rem;
  line-height: 1.5;
  color: var(--text-secondary);
  
  i {
    color: var(--primary-color);
    margin-right: 8px;
  }
  
  strong {
    color: var(--accent-color);
    font-weight: 500;
  }
`;

const AnalyzeButton = styled.div`
  padding: 10px 25px;
  background: linear-gradient(45deg, var(--secondary-color) 0%, #ffaa00 100%);
  color: #170032;
  border-radius: 30px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: auto;
  box-shadow: 0 5px 15px rgba(255, 208, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(255, 208, 0, 0.4);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  i {
    margin-left: 5px;
  }
`;

const ResultsAreaContainer = styled.div`
  margin-top: 40px;
  padding: 25px;
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #ffffff;
`;

const LoadingIndicator = styled.p`
  font-size: 1.1rem;
  color: #ffbe00;
  text-align: center;
`;

const ErrorDisplay = styled.div`
  color: #ff4f4f;
  background-color: rgba(255, 79, 79, 0.1);
  padding: 15px;
  border-radius: 10px;
  font-size: 1rem;
  text-align: center;
  white-space: pre-wrap;
`;

const ResultsTextContainer = styled.div`
  h3 {
    color: #ff3eb5;
    margin-bottom: 15px;
  }
  pre {
    white-space: pre-wrap;
    word-break: break-all;
    background-color: rgba(0,0,0,0.2);
    padding: 15px;
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.9);
    font-family: 'Courier New', Courier, monospace;
    font-size: 0.9rem;
  }
`;

const ImagePreviewContainer = styled.div`
  position: relative;
  width: calc(50% - 5px);
  aspect-ratio: 1;
  margin-bottom: 10px;

  @media (max-width: 600px) {
    width: calc(100% - 5px);
  }
`;

const ImagePreviewElement = styled.div`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 8px;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  padding: 0;

  &:hover {
    background-color: rgba(255, 0, 0, 0.8);
  }
`;

const ErrorMessageStyles = styled.div`
  color: #ff4f4f;
  background-color: rgba(255, 79, 79, 0.1);
  padding: 12px;
  border-radius: 10px;
  font-size: 0.9rem;
  margin-top: 15px;
  text-align: center;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 20px;
  padding: 30px;
  margin-bottom: 40px;
  position: relative;
  overflow-x: hidden;
`;

const ANALYSIS_COST = 250;

const AnalysisForm = ({ onAnalysisStateChange, analysisState, onAnalysisComplete }) => {
  const { currentUser, fetchUser, updateCredits } = useAuth();
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [context, setContext] = useState('general');
  const [specificGoal, setSpecificGoal] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dropAreaRef = useRef(null);
  const MAX_FILES = 6;

  const { results, analysisError, showResults } = analysisState;

  useEffect(() => {
    imagePreviews.forEach(url => URL.revokeObjectURL(url));
    const newPreviews = images.map(file => URL.createObjectURL(file));
    setImagePreviews(newPreviews);
    return () => newPreviews.forEach(url => URL.revokeObjectURL(url));
  }, [images]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesInternal(e.dataTransfer.files);
    }
  };

  const handleFilesInternal = (newlySelectedFiles) => {
    const fileArray = Array.from(newlySelectedFiles);
    if (fileArray.length === 0) return;
    const imageFilesOnly = fileArray.filter(file => file.type.startsWith('image/'));
    if (imageFilesOnly.length === 0) {
      alert('Please upload only image files (PNG, JPG, WebP).');
      return;
    }
    setImages(prevImages => {
      const combined = [...prevImages, ...imageFilesOnly];
      const limited = combined.slice(0, MAX_FILES);
      if (combined.length > MAX_FILES && prevImages.length < MAX_FILES) {
        alert(`You can upload a maximum of ${MAX_FILES} images. Only the first ${MAX_FILES - prevImages.length} new images were added.`);
      } else if (prevImages.length >= MAX_FILES && imageFilesOnly.length > 0) {
        alert(`You have already reached the maximum of ${MAX_FILES} images. No new images were added.`);
        return prevImages;
      }
      return limited;
    });
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      handleFilesInternal(e.target.files);
    }
  };

  const handleRemoveImage = (indexToRemove) => {
    setImages(prevImages => prevImages.filter((_, index) => index !== indexToRemove));
  };

  const handleAnalyzeClick = async () => {
    if (images.length === 0) {
      setError('Please upload at least one image.');
      if (onAnalysisStateChange) {
        onAnalysisStateChange({
          loading: false,
          results: null,
          analysisError: 'Please upload at least one image.',
          showResults: false
        });
      }
      return;
    }

    if (!currentUser) {
        setError('User not available. Please log in.');
        if (onAnalysisStateChange) {
            onAnalysisStateChange({
                loading: false,
                results: null,
                analysisError: 'User not available. Please log in.',
                showResults: false
            });
        }
        return;
    }

    if (currentUser.credits < ANALYSIS_COST) {
        const errorMsg = `Insufficient credits. You need ${ANALYSIS_COST} credits for an analysis. Current credits: ${currentUser.credits}`;
        setError(errorMsg);
        if (onAnalysisStateChange) {
            onAnalysisStateChange({
                loading: false,
                results: null,
                analysisError: errorMsg,
                showResults: false
            });
        }
        return;
    }

    setIsLoading(true);
    setError(null);
    if (onAnalysisStateChange) {
      onAnalysisStateChange({
        loading: true,
        results: null,
        analysisError: null,
        showResults: false
      });
    }

    try {
      const response = await analyzeProfile(images, context, specificGoal);
      console.log("AnalysisForm: analyzeProfile response:", response);

      if (response && response.analysis && response.success) {
        if (onAnalysisStateChange) {
          onAnalysisStateChange({
            loading: false,
            results: response.analysis,
            analysisError: null,
            showResults: true
          });
        }
        
        if (updateCredits) {
            updateCredits(currentUser.credits - ANALYSIS_COST);
        }

        const historyEntry = {
          context: context,
          specific_goal: specificGoal,
          results: response.analysis, 
          image_summary: {
            count: images.length,
            filenames: images.map(f => f.name || 'uploaded_image')
          },
          cost: ANALYSIS_COST,
          timestamp: new Date().toISOString(),
        };
        console.log("AnalysisForm: historyEntry created:", historyEntry);

        const token = localStorage.getItem('token');

        if (token) {
          try {
            console.log("AnalysisForm: Attempting to save analysis to history via API...");
            const saveResponse = await saveAnalysisToHistory(historyEntry, token);
            console.log("AnalysisForm: saveAnalysisToHistory Response:", saveResponse);

            if (saveResponse && saveResponse.analysis_id) {
               console.log("Analysis saved successfully with ID:", saveResponse.analysis_id);
            } else {
               console.warn("AnalysisForm: Save to history failed or returned unexpected response:", saveResponse);
            }
          } catch (saveError) {
             console.error("AnalysisForm: Error calling saveAnalysisToHistory API:", saveError);
          }
        } else {
           console.warn("AnalysisForm: No authentication token found, cannot call save analysis API.");
        }

        if (fetchUser) {
            await fetchUser(); 
        }
        
        if (onAnalysisComplete) {
            onAnalysisComplete(response.analysis);
        }

      } else {
        console.warn("AnalysisForm: analyzeProfile returned success=false or missing required fields:", response);
        const errorMessage = (response && response.error) || (response && response.message) || 'Error processing analysis results.';
        setError(errorMessage);
        if (onAnalysisStateChange) {
          onAnalysisStateChange({ loading: false, results: null, analysisError: errorMessage, showResults: false });
        }
      }
    } catch (error) {
      console.error("AnalysisForm: analyzeProfile API Error:", error);
      let detailedError = 'Si è verificato un errore durante la comunicazione con il server di analisi.';
      if (error.message) detailedError = error.message;
      if (error.response && error.response.data && error.response.data.message) detailedError = error.response.data.message;
      setError(detailedError);
      if (onAnalysisStateChange) {
        onAnalysisStateChange({ loading: false, results: null, analysisError: detailedError, showResults: false });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainContent>
      <UploadSection>
        <PhoneMockup>
          <PhoneHeader>
            <i className="fab fa-instagram"></i>
            <span>Preview</span>
          </PhoneHeader>
          <PhoneContent 
            ref={dropAreaRef} 
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={dragging ? 'drag-over' : ''}
          >
            {imagePreviews.length === 0 ? (
              <UploadPlaceholder className={dragging ? 'drag-over' : ''}>
                <i className="fas fa-cloud-upload-alt"></i>
                <p>Upload profile screenshots or drop files here (Max {MAX_FILES})</p>
              </UploadPlaceholder>
            ) : (
              imagePreviews.map((previewUrl, index) => (
                <ImagePreviewContainer key={index}>
                  <ImagePreviewElement style={{ backgroundImage: `url(${previewUrl})` }} />
                  <RemoveButton onClick={() => handleRemoveImage(index)}>X</RemoveButton>
                </ImagePreviewContainer>
              ))
            )}
          </PhoneContent>
          <PhoneFooter>
            <ActionButton onClick={handleUploadClick}>
              <span>Upload More</span>
              <i className="fas fa-cloud-upload-alt"></i>
            </ActionButton>
          </PhoneFooter>
        </PhoneMockup>
        
        <InputSection>
          <SectionTitle>Customize your analysis</SectionTitle>
          
          <InputGroup>
            <Label htmlFor="contextType">Context</Label>
            <Select 
              id="contextType" 
              value={context}
              onChange={(e) => setContext(e.target.value)}
            >
              <option value="general">General</option>
              <option value="romantic">Romantic date</option>
              <option value="professional">Professional collaboration</option>
              <option value="friendship">New friendship</option>
              <option value="networking">Networking</option>
            </Select>
          </InputGroup>
          
          <InputGroup>
            <Label htmlFor="specificGoal">Specific goal (optional)</Label>
            <TextArea 
              id="specificGoal" 
              placeholder={`Enter a specific goal for the analysis, for example: 'Analyze the presence of hobbies or sports interests' (Max ${MAX_FILES} images)`}
              value={specificGoal}
              onChange={(e) => setSpecificGoal(e.target.value)}
            />
          </InputGroup>
          
          <DisclaimerBox>
            <i className="fas fa-info-circle"></i>
            Please note: This analysis is based solely on visual content from social media profiles. 
            People may present themselves differently online than in real life. 
            <strong> This is not a certified professional analysis</strong> and should be used for entertainment purposes only.
          </DisclaimerBox>
          
          <input 
            type="file" 
            id="imageUpload" 
            ref={fileInputRef}
            multiple 
            accept="image/png, image/jpeg, image/webp"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          
          <AnalyzeButton 
            onClick={handleAnalyzeClick}
            disabled={isLoading || images.length === 0}
          >
            <span>
              {isLoading ? (
                'Analyzing...'
              ) : (
                <>
                  Analyze &nbsp;
                  <i className="fas fa-coins" style={{ marginRight: '4px', fontSize: '0.9em' }}></i> 
                  {ANALYSIS_COST}
                  &nbsp;
                </>
              )}
            </span>
            {!isLoading && <i className="fas fa-arrow-right"></i>}
          </AnalyzeButton>
          
          {error && (
            <ErrorMessageStyles>
              {error}
            </ErrorMessageStyles>
          )}
        </InputSection>
      </UploadSection>
      
      {/* Results rendering is handled by the parent component via onAnalysisStateChange */}
    </MainContent>
  );
};

export default AnalysisForm; 